import { NgClass, NgIf } from "@angular/common";
import { Component, DestroyRef, ElementRef, NgZone, OnInit, Renderer2, ViewChild, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import { Firestore, doc, getDoc } from "@angular/fire/firestore";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { ActivatedRoute, Data, Params, Router, RouterLink } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { CaptchaService } from "@core/captcha.service";
import { LoadingService } from "@core/loading.service";
import { UtilityService } from "@core/utility.service";
import { loginCredentials } from "@interfaces/log-credentials.i";
import { FlagInterface, Language, allLanguageCodes, defaultLanguage } from "@interfaces/translation-interface";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import {
  FirebaseUISignInFailure,
  FirebaseUISignInSuccessWithAuthResult,
  FirebaseuiAngularLibraryComponent
} from "firebaseui-angular";
import { Subject, Subscription, from, of } from "rxjs";
import { map, switchMap, take, takeUntil } from "rxjs/operators";
import { CommonVariable } from "src/app/app.constants";
import { CommonService } from "src/app/services/common/common.service";
import { GTMSagmentService } from "src/app/services/gtm-sagment.service";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { UserManagementService } from "src/app/services/user-management.service";
import { ForgotPasswordPage } from "../forgot-password/forgot-password.page";
import { ResetPasswordPopupComponent } from "../reset-password-popup/reset-password-popup.component";
import { VerifyPhoneNumberComponent } from "../verify-phone-number/verify-phone-number.component";
import { InitialUserContextService } from "src/app/services/initial-user-context.service";
import { CampaignTrackingService } from 'src/app/services/campaign-tracking.service';
import { RouteTrackerService } from "src/app/services/route-tracker.service";
import { ClarityService } from "src/app/services/clarity.service";
@Component({
  selector: "app-login",
  templateUrl: "./login.page.html",
  styleUrls: ["./login.page.scss"],
  standalone: true,
  imports: [
    IonicModule,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    TranslatePipe,
    NgIf,
    FirebaseuiAngularLibraryComponent
  ],
})
export class LoginPage implements OnInit {
  // service injection using inject()
  private fb = inject(FormBuilder);
  private bottomSheet = inject(MatBottomSheet);
  private authService = inject(AuthService);
  private analytics = inject(AngularFireAnalytics);
  private captchaService = inject(CaptchaService);
  public translate = inject(TranslateService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private ngZone = inject(NgZone);
  private GTMSegmentService = inject(GTMSegmentService);
  private loadingService = inject(LoadingService);
  private userManagementService = inject(UserManagementService);
  private utilityService = inject(UtilityService);
  private firestore = inject(Firestore);
  private commonService = inject(CommonService);
  private campaignTrackingService = inject(CampaignTrackingService);
  private routeTrackerService = inject(RouteTrackerService);
  private clarityService = inject(ClarityService);
  private destroyRef = inject(DestroyRef);
  private initialUserContextService = inject(InitialUserContextService);

  isSpoofMsn: boolean;
  uid: string;
  resetToken: string;
  passwordType = "password";
  passwordIcon = "eye-off";
  selectedLanguage: string = localStorage?.getItem("language_code") || "en";
  loginForm: FormGroup;
  selectedFlag: FlagInterface;
  isShowDiv: Boolean = true;
  cell1TelInput = {
    initialCountry: CommonVariable.countryCode,
    separateDialCode: true,
  };
  phoneInValid: boolean = false;
  isCaptchaClickable = true;
  countryCode: string;
  incorrectPhoneNumber = false;
  isLanguageChangeTrigger: boolean;
  redirectTo: string;
  @ViewChild("phoneNumber", { static: true }) phoneNumber: ElementRef;
  languageArray: {
    language_code: string;
    language_id: number;
    language_name: string;
    language_status: boolean;
  }[] = [];
  hideCapture = false;
  msn: string;
  loginAttempt: number;
  localStorage = window.localStorage;
  newPhoneNumber: string;
  newDialCode: string;
  subscription: Subscription;
  selectedIPLanguage: string;
  selectedLanguageSubscription: Subscription;
  private viewDestroy$ = new Subject<void>();
  formType: boolean;
  private unsubscribe$ = new Subject<void>();
  issToken = signal(false)
  userId: string;
  private isComponentActive = false;
  constructor() { }
  ngOnInit() {
    this.uid = "";
    this.loginAttempt = 0;
    this.isComponentActive = true;
    const context = this.initialUserContextService.resolveInitialContext();
    this.selectedLanguage = context.language.value;
    this.selectedIPLanguage = context.language.value;

    this.utilityService
      .getLanguageList()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (l: Data) => {
          if (!l || !l?.languages || l?.languages?.length === 0) return;
          this.languageArray = l?.languages?.filter(language => language?.language_status);
          this.syncLanguageDropdownSelection();
        },
        (err) => {
          return err;
        }
      );
    if (this.analytics != null && this.analytics != undefined) {
      this.analytics?.logEvent("what's_on_screen", {
        userId: this.userId,
      });
    }

    this.GTMSegmentService.collectVisitorData();
    // Removed problematic code that extracted dial code from intl-tel-input library's internal DOM
    // element (.iti__selected-dial-code) and set it via setValue(). This was bypassing the library's
    // proper state management and causing malformed event objects, which resulted in ReferenceError
    // in Cloudflare Turnstile. The dial code should be managed through the library's API or the
    // onCell1CountryChange event handler instead.

    this.validateLoginForm();

    const localStorageLanguage: Language = this.localStorage?.getItem("language_code") as Language;
    if (localStorageLanguage && allLanguageCodes.includes(localStorageLanguage)) {
      this.onLanguageChange(localStorageLanguage);
    } else {
      this.syncLanguageDropdownSelection();
    }
  }

  private syncLanguageDropdownSelection(): void {
    if (!this.languageArray?.length) {
      return;
    }

    const availableCodes = this.languageArray.map((language) => language.language_code);
    let current =
      this.selectedLanguage ||
      this.localStorage?.getItem("language_code") ||
      this.initialUserContextService.getContext()?.language.value ||
      defaultLanguage;

    if (!availableCodes.includes(current)) {
      current = availableCodes.includes(defaultLanguage) ? defaultLanguage : availableCodes[0];
      this.onLanguageChange(current as Language);
      return;
    }

    this.selectedLanguage = current;
    this.selectedIPLanguage = current;
  }


  ionViewWillEnter() {
    this.isComponentActive = true;
    this.viewDestroy$ = new Subject<void>();
    this.redirectTo = this.route.snapshot.queryParams["redirectTo"] || "/mainTabs/tabs/home";

    if (this.router.url.includes("forgottenpassword")) {
      this.openForgetPassword();
    }

    this.route.queryParams
      .pipe(takeUntil(this.viewDestroy$))
      .subscribe({
        next: (params) => {
          if (!this.isComponentActive) {
            return;
          }
          this.handleQueryParams(params);
        },
        error: (err) => {
          // [LOTTOSOCIAL-PWA-1NCT] Do not re-throw here. Throwing inside an RxJS error
          // callback creates an unhandled promise rejection that bypasses Angular's
          // GlobalErrorHandler (and its minified-error suppression). Log only.
          this.authService.errorHandlerLogs('[LoginPage] route.queryParams error:', err);
        },
      });

    if (this.analytics != null && this.analytics != undefined) {
      try {
        this.captchaService?.captchaSolved$?.pipe(takeUntil(this.viewDestroy$)).subscribe(
          (data) => {
            if (!this.isComponentActive) {
              return;
            }
            if (data && location.href.search("login") > -1) {
              this.isCaptchaClickable = true;
              this.openForgetPassword();
            }
          },
          (err) => {
            return err;
          }
        );
      } catch (e) {
        return e;
      }
    }

    if (this.selectedLanguageSubscription) {
      this.selectedLanguageSubscription.unsubscribe();
    }
    this.selectedLanguageSubscription = this.authService?.selectedlanguage$?.pipe(takeUntil(this.viewDestroy$)).subscribe({
      next: (selectedLanguage) => {
        if (!this.isComponentActive) {
          return;
        }
        const defaultLanguage = 'en';
        if (!this.flag || !this.flag?.length) {
          this.authService.errorHandlerLogs("Language flags not initialized yet, skipping language selection")
          return;
        }

        const selectedFlag = this.flag?.find((flag) => flag?.country_code === selectedLanguage);
        this.selectedFlag =
          selectedFlag || this.flag.find((flag) => flag?.country_code === defaultLanguage) || this.flag[0];
        if (selectedFlag) {
          this.cell1TelInput.initialCountry =
            selectedLanguage !== defaultLanguage ? selectedLanguage : CommonVariable.countryCode;
        }
      },
      error: (error) => {
        this.authService.errorHandlerLogs("Error in selectedLanguageSubscription:", error)
      }
    });

    void this.initialUserContextService.confirmFromIpInBackground();
    this.loadLoginIpDefaults();
  }

  ionViewWillLeave() {
    this.isComponentActive = false;
    this.viewDestroy$.next();
    this.viewDestroy$.complete();
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    if (this.selectedLanguageSubscription) {
      this.selectedLanguageSubscription.unsubscribe();
      this.selectedLanguageSubscription = null;
    }
  }

  private handleQueryParams(params: Params) {
    if (!this.isComponentActive) {
      return;
    }

    try {
      // Apply language from cross-domain handoff (e.g. GiveawayKing redirect).
      // Mark as explicit so IP/edge confirmation cannot overwrite localStorage with browser locale (e.g. hi).
      const langFromUrl = (params?.lang_code || params?.language_code) as Language | undefined;
      if (langFromUrl && allLanguageCodes.includes(langFromUrl)) {
        this.initialUserContextService.resolveInitialContext({ explicitLanguage: langFromUrl });
        this.onLanguageChange(langFromUrl);
      }
      const handoff = params?.handoff;
      if (handoff) {
        this.issToken.set(true);
        const handoffCode = decodeURIComponent(handoff);
        // Strip handoff from the address bar immediately (single-use code must not linger)
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("handoff");
          window.history.replaceState({}, "", url.pathname + url.search + url.hash);
        } catch {
          // ignore history errors
        }
        if (handoffCode) {
          this.authService.autoLogin(null, { ...params, handoff: handoffCode });
          sessionStorage.removeItem("authToken");
          sessionStorage.removeItem("paramData");
          this.loadingService.present();
          return;
        }
        throw new Error("Invalid handoff format");
      }

      const token = params?.token;
      if (token) {
        this.issToken.set(true);
        const accessToken = decodeURIComponent(token);
        if (accessToken) {
          this.authService.autoLogin(accessToken, params);
          sessionStorage.removeItem("authToken");
          sessionStorage.removeItem("paramData");
          this.loadingService.present();
        } else {
          throw new Error("Invalid token format");
        }
      }
    } catch (error) {
      throw new Error(`Error: ${error}`);
    }
  }


  onLanguageChange(language: Language) {
    try {
      language = allLanguageCodes.includes(language) ? language : defaultLanguage;
      const prevLocalLang = localStorage.getItem("language_code") || "";
      this.userManagementService.setUserLanguageLogInFb(prevLocalLang, language, "login")
      this.localStorage.setItem("language_code", language);

      if (this.authService) {
        this.authService.selectedlanguage$.next(language ? language : defaultLanguage);
        this.authService.isLanguageChangeTrigger$.next(true);
        this.isLanguageChangeTrigger = true;
      }
      this.selectedLanguage = language;
      this.translate.use(language);
      // this.NotificationsService?.sendLanguageRequest(language ? language : defaultLanguage);
    } catch (err) {
      return err;
    }
  }

  toggleViewPassword(): void {
    this.passwordType = this.passwordType === "text" ? "password" : "text";
    this.passwordIcon = this.passwordIcon === "eye" ? "eye-off" : "eye";
  }

  onSubmit(value: loginCredentials, valid: boolean) {
    if (!valid) {
      this.showValidationErrors();
      this.GTMSegmentService.collectVisitorData("", "", 1, 0);
      return;
    }
    this.loginAttempt++;
    const normalizedPhoneNumber = this.normalizePhoneNumber(value.phoneNumber, value.dialCode);
    this.authService.redirectTo = this.redirectTo;
    this.authService.captureDeviceInfo();
    this.authService
      .tryLoginWithCred({
        ...value,
        phoneNumber: normalizedPhoneNumber
      })
      .then(() => {
        // Login success
        this.GTMSegmentService.collectVisitorData("", "", 1, 1);
        this.authService.getUid("login-success").then((memberId) => {
          if (!memberId || memberId === "null") return;
          this.clarityService.identify(memberId);
          this.clarityService.event("login_success");
          this.clarityService.setGa4UserId(memberId);
          this.clarityService.ga4Event("login", { method: "password_or_magic_link" });
        });
        const campaignId = this.campaignTrackingService.getLatestCampaignId();
        if (campaignId) {
          // If campaign ID exists, always redirect to account page with campaign_id
          const accountUrl = this.campaignTrackingService.appendCampaignIdToUrl('/account');
          this.router.navigateByUrl(accountUrl);
          return;
        }
        this.routeTrackerService.navigateToReturnUrl();
      })
      .catch((error) => {
        // Login failed
        this.GTMSegmentService.collectVisitorData("", "", 1, 0);
        if (this.loginAttempt > 2) this.openForgetPassword();
        return error;
      });
    sessionStorage.setItem("toastStatus", "true");
  }

  private showValidationErrors(): void {
    const userLanguage = this.selectedLanguage || 'en';
    const docRef = doc(this.firestore, '/user_error_message/login_error_messages');

    from(getDoc(docRef))
      .pipe(
        map(snapshot => snapshot.data()?.[userLanguage] || {})
      )
      .pipe(takeUntil(this.viewDestroy$)).subscribe(errorMessages => {
        const LoginFailed =
          errorMessages?.['wrong-password'] ||
          'Login failed. Please check your credentials and try again.';

        const defaultMsg = {
          message: {
            line_1: LoginFailed,
            line_2: '',
          },
          style: {
            icon: 'tick circle error.svg',
            type: 'Error',
          },
        };

        this.authService?.showToastMessage({}, defaultMsg);
      });
  }

  private normalizePhoneNumber(phoneNumber: string, dialCode: string): string {
    if (phoneNumber.startsWith("0")) {
      phoneNumber = phoneNumber.substring(1);
    }
    if (!phoneNumber.startsWith(dialCode)) {
      phoneNumber = dialCode + phoneNumber;
    }
    return phoneNumber;
  }

  private validateLoginForm() {
    this.loginForm = this.fb.group({
      phoneNumber: ["", Validators.compose([Validators.required, Validators.maxLength(11)])],
      password: ["", [Validators.required, Validators.minLength(6)]],
      dialCode: ["", Validators.required],
    });
  }

  openVerifyPhoneNumber(res) {
    this.msn = res?.msn;
    this.uid = res?.uid;
    const verifyPhoneNoBottomSheet = this.bottomSheet.open(VerifyPhoneNumberComponent, {
      panelClass: "verify-phone-number-bottom-sheet",
      disableClose: true,
      data: {
        msn: res?.msn,
        type: "signin",
      },
    });
    try {
      verifyPhoneNoBottomSheet?.afterDismissed()?.pipe(takeUntil(this.viewDestroy$)).subscribe(
        (data) => {
          if (data) {
            this.msn = data?.data?.msn ?? res?.msn;
            if (data?.data?.spoof_msn) {
              this.isSpoofMsn = data?.data?.spoof_msn;
            }
            if (data?.data?.uid) {
              this.uid = data?.data?.uid;
            }
            if (data?.resetToken) {
              this.resetToken = data.resetToken;
            }
            this.resetPassword();
          } else if (data === false) {
            this.captchaService?.otpSent$.next(false);
            this.authService
              ?.getConstantsMsg("1010")
              .then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "That verification code is incorrect. Check the code and try again.",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                this.authService?.showToastMessage(translatedMsg, defaultMsg);
              })
              .catch((error) => {
                return error;
              });
          }
        }
      );
    } catch (error) {
      return error;
    }
  }

  openForgetPassword() {
    const forgotPasswordDialog = this.bottomSheet.open(ForgotPasswordPage, {
      panelClass: "forgot-password-bottomsheet",
      hasBackdrop: true,
      disableClose: true,
      backdropClass: "about-product-dialog-backdrop",
      data: {
        countryCode: this.countryCode,
        mobileNumber: this.loginForm?.get("phoneNumber")?.value,
      },
    });
    try {
      forgotPasswordDialog?.afterDismissed()?.pipe(takeUntil(this.viewDestroy$)).subscribe(
        (res: Data) => {
          if (res === undefined) {
            return;
          }
          if (res) {
            this.openVerifyPhoneNumber(res);
          }
        },
        (err) => {
          return err;
        });
    } catch (e) {
      return e;
    }
  }

  /* Reset password bottom sheet */
  resetPassword(): void {
    this.bottomSheet.open(ResetPasswordPopupComponent, {
      disableClose: true,
      data: { msn: this.msn, resetToken: this.resetToken },
    });
  }

  getPhoneNumber(event) {
    this.newPhoneNumber = event?.target?.value;
    const maxLength = 11;
    if (this.newPhoneNumber?.length > maxLength) {
      this.newPhoneNumber = this.newPhoneNumber.slice(0, maxLength);
    }
    const phoneNumberRegex = /^0?\d+$/;
    const isPhoneNumberValid = phoneNumberRegex.test(this.newPhoneNumber);

    const displayedPhoneNumber = this.newPhoneNumber;
    this.loginForm?.get("phoneNumber")?.setValue(displayedPhoneNumber);

    // Set or clear the error based on the phone number validity
    if (!isPhoneNumberValid) {
      this.loginForm?.get("phoneNumber")?.setErrors({ incorrectPhoneNumber: true });
    } else {
      this.loginForm?.get("phoneNumber")?.setErrors(null);
    }
  }
  onCell1CountryChange(event) {
    // Extract event data outside Angular zone to prevent Java bridge errors on Android WebView
    this.countryCode = event?.iso2;
    this.newDialCode = event?.dialCode;
    
    // Only update form if component is still active to prevent Java bridge errors during navigation
    if (!this.isComponentActive) {
      return;
    }
    
    // Process the form update inside Angular zone to ensure proper event handling
    this.ngZone.run(() => {
      this.loginForm?.get("dialCode")?.setValue(this.newDialCode);
    });
  }

  phoneNumberHasError(hasError: boolean) {
    this.incorrectPhoneNumber = hasError;
    this.loginForm?.get("phoneNumber")?.setErrors({
      incorrectPhoneNumber: !hasError,
    });
    this.phoneInValid = this.phoneNumber?.nativeElement?.value ? true : false;
  }

  numericOnly(event): boolean {
    const charcode = event?.which ? event?.which : event?.keyCode;
    if (charcode >= 48 && charcode <= 57) {
      return true;
    }
    return false;
  }
  toggleDisplayDiv() {
    this.isShowDiv = !this.isShowDiv;
  }
  onLanguageSelect(flag: FlagInterface) {
    this.selectedFlag = flag;
    this.onLanguageChange(flag.country_code);
  }

  flagsDropdownToggle(value: boolean) {
    CommonVariable.flagsDropdownOpened = value;
  }

  flag: FlagInterface[] = [
    { id: "1", country_code: Language.English, name: "English (default)", img: "../../../assets/images/kingdom.png" },
    { id: "2", country_code: Language.Italian, name: "Italiana", img: "../../../assets/images/italian.png" },
    { id: "3", country_code: Language.German, name: "Deutsche", img: "../../../assets/images/deuch.jpg" },
    { id: "4", country_code: Language.French, name: "Français", img: "../../../assets/images/france.png" },
    { id: "5", country_code: Language.Spanish, name: "Españo la", img: "../../../assets/images/spain.png" },
  ];

  ngAfterViewInit() {
    // IP defaults are loaded in ionViewWillEnter (view-scoped). Only DOM-dependent
    // styling belongs here to avoid a duplicate getUserCountryBaseOnIP subscription.
    this.hideElements();
    this.updateContainerStyle();
  }

  private loadLoginIpDefaults(): void {
    if (!this.loginForm) {
      return;
    }

    const context = this.initialUserContextService.getContext()
      ?? this.initialUserContextService.resolveInitialContext();
    this.loginForm.get("dialCode")?.setValue(context.dialCode.value);

    try {
      if (this.subscription) {
        this.subscription.unsubscribe();
      }

      this.subscription = this.userManagementService
        .getUserCountryBaseOnIP()
        .pipe(
          takeUntil(this.viewDestroy$),
          switchMap((res: Data) => {
            const countryCode = res?.country_code ?? context.countryCode.value;
            const ipstackCountryCode =
              "+" + (res?.location?.calling_code ? res?.location?.calling_code : CommonVariable.defaultDialCode);

            const dialCodeControl = this.loginForm?.get("dialCode");
            if (dialCodeControl && !dialCodeControl.dirty) {
              dialCodeControl.setValue(ipstackCountryCode);
            }

            return this.authService.getCurrencyAndLanguageList$.pipe(
              map((data: Data) => {
                const countriesArray = Object.keys(data);
                if (countryCode !== "" && countriesArray.includes(countryCode)) {
                  return data[countryCode] ?? data["GB"];
                }
                return data["GB"];
              })
            );
          })
        )
        .subscribe((data: Data) => {
          const latestContext = this.initialUserContextService.getContext() ?? context;
          // Respect URL / user-explicit language — do not let IP defaults clobber lang_code=en
          if (latestContext.language.provenance === "explicit" || latestContext.language.dirty) {
            this.selectedLanguage = latestContext.language.value;
            this.syncLanguageDropdownSelection();
            return;
          }

          const localLanguage = this.localStorage?.getItem("language_code") ?? latestContext.language.value;
          const ipLanguage = data?.language ?? latestContext.language.value;
          this.selectedIPLanguage = ipLanguage;
          this.onLanguageChange((localLanguage || ipLanguage || "en") as Language);
          this.syncLanguageDropdownSelection();
        });
    } catch (err) {
      return err;
    }
  }
  private hideElements() {
    const elementsToHide = this.el.nativeElement.querySelectorAll(".iti__flag.iti__gb");
    elementsToHide.forEach((element) => {
      const htmlElement = element as HTMLElement;
      htmlElement.style.setProperty("display", "none", "important");
    });

    // Set a specific width for the container
    const container = this.el.nativeElement.querySelector(".iti.iti--allow-dropdown"); // Replace with the actual container class or element selector
    if (container) {
      const containerElement = container as HTMLElement;
      containerElement.style.width = "10%";
    }
  }

  updateContainerStyle(): void {
    const container = this.el.nativeElement.querySelector(
      ".iti--allow-dropdown .iti__flag-container, .iti--separate-dial-code .iti__flag-container"
    );

    if (container) {
      this.renderer.setStyle(container, "right", "20%");
    }
  }

  checkInput(e) {
    let value;
    const currentInput = e?.target?.value;
    const DEFAULT_VALUE = "+";
    const currentInputDefaultPart = currentInput.substring(0, DEFAULT_VALUE?.length);
    // check if the default part is no longer same as DEFAULT
    if (currentInputDefaultPart !== DEFAULT_VALUE) {
      // reset
      value = DEFAULT_VALUE;
      this.loginForm?.get("dialCode")?.setValue(value);
    }
  }

  // This method is used for Google Analytics
  hotJarEvents(event: string) {
    this.GTMSegmentService.HotJarEvent(event);
  }

  async successCallback(signInSuccessData: FirebaseUISignInSuccessWithAuthResult) {
    const socialUser = signInSuccessData?.authResult?.user?.providerData?.[0];
    const user = this.getSocialUser(socialUser);

    const userAuth = await this.authService?.getCurrentLoggedinUser();
    const uid: string = userAuth?.uid;

    if (uid && !uid?.startsWith("DFCI")) {
      await userAuth?.delete().catch((error) => {
        return error;
      });
    }

    this.callSocialLogin(user);
  }

  getSocialUser(socialUser): Data {
    return {
      connect_type: socialUser?.providerId,
      social_id: socialUser?.uid,
      email: socialUser?.email,
    };
  }

  errorCallback(errorData: FirebaseUISignInFailure) {
    try {
      console.info("ERROR", errorData);
    } catch (e) {
      // Suppress errors from FirebaseUI/GAPI frames initialization
    }
  }

  uiShownCallback() {
    console.log("UI SHown");
  }

  callSocialLogin(request: Data): void {
    this.loadingService.present();
    const providerLabel =
      request?.connect_type === "google.com"
        ? "Google"
        : request?.connect_type === "facebook.com"
          ? "Facebook"
          : request?.connect_type || "social";
    this.authService
      .callSigInSocial(request)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res: Data) => {
          if (res?.response?.status === "FAIL") {
            // Social login failure
            this.GTMSegmentService.collectVisitorData("", "", 1, 0);
            this.loadingService.present();
            this.authService
              ?.getConstantsMsg("1152")
              .then((translatedMsg) => {
                let translatedMessage = translatedMsg?.message;
                if (translatedMessage?.line_1?.includes("{provider}")) {
                  translatedMessage = {
                    ...translatedMessage,
                    line_1: translatedMessage.line_1.replace(/\{provider\}/g, providerLabel),
                  };
                }
                const defaultMsg = {
                  message: {
                    line_1: res?.response?.message || `We couldn't log you in with ${providerLabel}. Try again.`,
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                this.authService?.showToastMessage(
                  translatedMessage ? { ...translatedMsg, message: translatedMessage } : translatedMsg,
                  defaultMsg
                );
              })
              .catch((error) => {
                return error;
              });
            this.authService?.logout().catch((error) => error);
            return;
          }
          if (res?.response?.status === "SUCCESS" && res?.response?.customer_id) {
            this.checkIfUserIdExist(res?.response);
          }
        },
        (err) => {
          // Network/API error during social login
          this.GTMSegmentService.collectVisitorData("", "", 1, 0);
          this.loadingService.dismiss();
          return err;
        }
      );
  }

  checkIfUserIdExist(response): void {
    this.commonService.getUid("CheckIfUserIdExist").pipe(
      takeUntil(this.unsubscribe$),
      switchMap((userId) => {
        const finalUserId = response?.user_id || userId;

        if (!finalUserId) {
          this.loadingService.dismiss();
          return of(null);
        }

        const docRef = doc(this.firestore, `users/${finalUserId}`);
        return from(getDoc(docRef)).pipe(
          map((snapshot) => ({
            snapshot,
            finalUserId,
          }))
        );
      })
    )
      .pipe(takeUntil(this.viewDestroy$)).subscribe({
        next: (res) => {
          if (!res) return;

          const userData = res.snapshot.data();
          const exists = res.snapshot.exists();

          // ✅ Call existing method
          this.createFbUser(userData, response, exists);
        },
        error: (error) => {
          this.loadingService.dismiss();
        },
      });
  }


  createFbUser(user, response: Data, existingUser: boolean): void {
    this.authService
      .createFbUser(user, response, existingUser)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (createFbUserRes: Data) => {
          if (createFbUserRes && createFbUserRes?.success && createFbUserRes?.customToken) {
            this.authService
              .socialLogin(createFbUserRes.customToken, user?.connect_type)
              .then(() => {
                // Redirect after successful login
                // window.location.reload();
                this.routeTrackerService.navigateToReturnUrl();
              })
              .catch((error) => {
                console.error("Error during social login:", error);
              });
          } else {
            return createFbUserRes?.message;
          }
        },
        (err) => {
          this.loadingService.dismiss();
          return err;
        }
      );
  }
   ngOnDestroy() {
    this.isComponentActive = false;
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.selectedLanguageSubscription) {
      this.selectedLanguageSubscription.unsubscribe();
    }
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.viewDestroy$.next();
    this.viewDestroy$.complete();
  }
}
